import React, { useEffect, useState } from 'react';

import { Box, Divider, HStack, Input, Stack, Textarea } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ExpandableText,
  PageHeaderWithIcon,
  renderTime,
  Tag,
} from 'components/DataDisplay';
import { DetailsField } from 'components/DataDisplay/Utils/DetailsField';
import {
  CancelButton,
  defaultFormStyles,
  PrimaryButton,
  PrimaryIconButton,
} from 'components/DataEntry';
import { EditIcon } from 'components/Icons';
import { useBlueprint } from 'containers/PolicyHub/Blueprints/Components/Blueprint/hooks/useBlueprint';
import {
  renderCustomTag,
  renderEditLock,
} from 'containers/PolicyHub/Blueprints/Components/utils';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { actions } from 'containers/PolicyHub/Blueprints/slice';
import { getBlueprintIcon } from 'containers/PolicyHub/utils';

export const BlueprintGeneralDetails = () => {
  const { getBlueprint } = useBlueprint();
  const dispatch = useDispatch();
  const { blueprint, blueprintAction } = useSelector(selectBlueprints);
  const [isEdit, setIsEdit] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    setName(blueprint.data.name);
    setDescription(blueprint.data?.description ?? '');
  }, [blueprint.data]);

  const handleOnClickSave = () => {
    dispatch(
      actions.updateBlueprint({
        q: {
          blueprintId: blueprint.data?.id,
          blueprintUpdate: {
            name,
            description: description ?? undefined,
          },
        },
        onSuccess: () => {
          getBlueprint();
          setIsEdit(false);
        },
      }),
    );
  };

  return (
    <Box w="full" h="full" py={4}>
      <Stack w="full" spacing={3} h="full">
        <HStack justify="space-between" w="full" px={6}>
          <HStack flex={1}>
            <PageHeaderWithIcon
              label={
                isEdit ? (
                  <Input
                    {...defaultFormStyles.textField?.input}
                    value={name}
                    onChange={e => setName(e.target.value)}
                  />
                ) : (
                  <HStack>
                    <Box>{blueprint.data?.name}</Box>
                    {renderCustomTag(blueprint.data?.created_by)}
                  </HStack>
                )
              }
              labelWidth="full"
              fontSize="lg"
              icon={getBlueprintIcon(
                blueprint.data.created_by !== 'system',
                blueprint.data?.compliance ?? blueprint.data?.name,
              )}
              iconFilled={false}
              reversed
              iconStyles={{ w: 12, h: 12, p: 0 }}
            />
            {blueprint.data.created_by === 'system' &&
              !!blueprint.data?.compliance && (
                <Tag
                  label="Compliance"
                  styles={{
                    label: { color: 'white' },
                    tag: { borderRadius: 'full', bg: 'primary' },
                  }}
                />
              )}
          </HStack>
          <HStack>
            {!isEdit && (
              <PrimaryIconButton
                aria-label="edit"
                isDisabled={blueprint.data?.created_by === 'system'}
                onClick={() => setIsEdit(true)}
                tooltip={
                  'Edit details' +
                  (blueprint.data?.created_by === 'system' ? ' disabled' : '')
                }
                bg="white"
                color="primary"
                maxW={10}
                w={10}
                h={10}
              >
                <EditIcon />
              </PrimaryIconButton>
            )}
            {isEdit && (
              <CancelButton
                label="Cancel"
                onClick={() => setIsEdit(false)}
                size="xs"
              />
            )}
            {isEdit && (
              <PrimaryButton
                size="xs"
                onClick={handleOnClickSave}
                isLoading={blueprintAction.isLoading}
              >
                Save
              </PrimaryButton>
            )}
          </HStack>
        </HStack>
        <Stack justify="space-between" h="full" minH={0} overflow="hidden">
          <Box
            w="full"
            h="full"
            minH={0}
            pt={1}
            pl={4}
            px={6}
            overflow="hidden"
          >
            <DetailsField
              label="Description"
              isInline={false}
              width="full"
              h="full"
              spacing={2}
              styles={{ value: { minH: 0, overflow: 'hidden' } }}
              value={
                isEdit ? (
                  <Textarea
                    {...defaultFormStyles.textAreaField?.textArea}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                  />
                ) : (
                  <ExpandableText noOfLines={5} expandedMaxHeight="10rem">
                    {blueprint.data?.description || '-'}
                  </ExpandableText>
                )
              }
            />
          </Box>
          <Divider borderColor="componentBorder" flexShrink={0} />
          <HStack
            w="full"
            justify="space-between"
            px={6}
            pt={1}
            spacing={0}
            flexShrink={0}
          >
            <DetailsField
              label="Created By"
              value={
                blueprint.data.created_by === 'system'
                  ? 'Ariksa'
                  : blueprint.data.created_by
              }
            />
            <HStack>
              <Box pb={1}>{renderEditLock(blueprint.data.created_by, 5)}</Box>
              <Box>
                {blueprint.data.created_by === 'system' ? 'Not ' : ''}Editable
              </Box>
            </HStack>
            <DetailsField
              label="Created At"
              value={renderTime({ value: blueprint.data.created_at })}
            />
            <DetailsField
              label="Updated At"
              value={renderTime({ value: blueprint.data.last_updated_at })}
            />
          </HStack>
        </Stack>
      </Stack>
    </Box>
  );
};
